import { useLoaderData, useOutletContext, useRevalidator } from "react-router-dom";
import { ContextType } from "../../ProjectWrapper";
import { Box, Button, Chip, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { SettingsHead } from "../../../../components/form/SettingsHead";
import React, { useContext } from "react";
import { MoreMenu } from "../../../../components/User/MoreMenu";
import { CustomClaimModal } from "./CustomClaimModal";
import { ApiClient } from "../../../../client/ApiClient";
import { ConfirmationModal } from "../../../../components/ConfirmationModal";
import { HankoDialog } from "../../../../components/HankoDialog";
import { SnackbarContext } from "../../../../components/Snackbar/SnackbarProvider";
import { Width } from "../../../../const/Width";
import { Project, SamlIdentityProvider } from "../../../../models/Project";

interface LoaderData {
  samlProviders: SamlIdentityProvider[]
}

// A connection referencing an in-use claim, kept distinct from its display name - two
// connections of different kinds (or even the same kind) can share a name, so the modal
// listing these needs kind alongside name to tell them apart. "Social"/"Enterprise" match the
// sidebar's own labels (navbarItems.tsx) for these two connection settings pages, rather than
// protocol names (OIDC is also too narrow - a custom "social" connection may be plain OAuth).
export interface ClaimReference {
  name: string
  kind: "Social" | "Enterprise"
}

// Hanko hard-rejects (400) any tenant config where a connection's mapping still references a
// claim name no longer present in custom_claims.definitions - so deletion of an in-use claim
// always fails server-side. Rather than let the user hit that 400 with no context, find who's
// referencing the claim up front and block the delete locally with a precise message.
//
// samlProviders must come from ApiClient.listSamlIdentityProviders (same source EnterpriseSSO.tsx
// uses), NOT project.saml.identity_providers - the latter only reflects the console's own stored
// tenant config JSON (tenantConfig.Saml), which is unrelated to the DB-backed SAML connections
// admins actually create/edit; it's always empty in multi-tenant mode, which silently made this
// check dead code for SAML.
const findClaimReferences = (project: Project, samlProviders: SamlIdentityProvider[], claimName: string): ClaimReference[] => {
  const references: ClaimReference[] = []
  for (const idp of samlProviders) {
    if (idp.attribute_map?.custom?.[claimName] !== undefined) {
      references.push({ name: idp.name, kind: "Enterprise" })
    }
  }
  for (const [id, mapping] of Object.entries(project.custom_provider_claim_mappings ?? {})) {
    if (mapping[claimName] !== undefined) {
      references.push({ name: project.third_party.custom_providers?.[id]?.display_name ?? id, kind: "Social" })
    }
  }
  return references
}

export const CustomClaims = () => {
  const { project, organization } = useOutletContext() as ContextType
  const { samlProviders } = useLoaderData() as LoaderData
  const [open, setOpen] = React.useState(false)
  const [claimName, setClaimName] = React.useState<string | undefined>(undefined)
  const revalidator = useRevalidator()
  const { success, error } = useContext(SnackbarContext)

  const deleteClaim = async (name: string) => {
    const customClaims = { ...project.custom_claims }
    delete customClaims[name]
    try {
      await ApiClient.updateProject(organization.id, project.id, {
        custom_claims: customClaims,
      })
      revalidator.revalidate()
      success("Custom claim deleted successfully")
    } catch (e) {
      console.log(e)
      error("Failed to delete custom claim")
    }
  }

  const startEditClaim = (name: string) => {
    setClaimName(name)
    setOpen(true)
  }

  let claimRows: React.ReactNode[] = []
  if (project.custom_claims) {
    for (const [name, definition] of Object.entries(project.custom_claims)) {
      claimRows.push(<ClaimTableRow key={name} name={name} type={definition.type} description={definition.description}
                                     referencedBy={findClaimReferences(project, samlProviders, name)}
                                     deleteClaim={deleteClaim} setClaimName={startEditClaim} />)
    }
  }

  if (claimRows.length === 0) {
    claimRows.push(<TableRow key="empty">
      <TableCell colSpan={100} size="medium"><Typography variant="body2" sx={{
        color: '#7C8E9C'
      }}>No custom claims found.</Typography></TableCell>
    </TableRow>)
  }

  return <Box maxWidth={Width.NORMAL} display="flex" flexDirection="column">
    <Box sx={{
      display: 'flex',
      flexDirection: 'row',
      gap: { xs: 2, md: 4 },
      justifyContent: 'space-between'
    }}>
      <SettingsHead title="Custom claims"
                    description="Declare tenant-wide claims that your SAML/OIDC connections can map their own attributes onto." />
      <Button variant="contained" onClick={() => setOpen(true)}>New claim</Button>
    </Box>
    <Table size="small" sx={{ mt: 4 }}>
      <TableHead>
        <TableRow>
          <TableCell>Name</TableCell>
          <TableCell>Type</TableCell>
          <TableCell>Description</TableCell>
          <TableCell sx={{ width: 0 }}></TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {claimRows}
      </TableBody>
    </Table>
    <CustomClaimModal organizationId={organization.id} project={project} name={claimName} open={open} onClose={() => {
      setOpen(false)
      setClaimName(undefined)
    }} />
  </Box>
}

interface TableRowProps {
  name: string
  type: string
  description?: string
  referencedBy: ClaimReference[]
  deleteClaim: (name: string) => Promise<void>
  setClaimName: (name: string) => void
}

const ClaimTableRow = (props: TableRowProps) => {
  const { name, type, description, referencedBy, setClaimName } = props
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false)
  const [blockedModalOpen, setBlockedModalOpen] = React.useState(false)
  const inUse = referencedBy.length > 0

  return <>
    <TableRow key={name}>
      <TableCell>{name}</TableCell>
      <TableCell sx={{ width: 0 }}>
        <Chip label={type} variant="outlined" sx={{ fontWeight: 500 }} />
      </TableCell>
      <TableCell>{description}</TableCell>
      <TableCell sx={{ width: 0 }}>
        <MoreMenu items={[{
          label: 'Edit',
          onClick: () => {
            setClaimName(name)
          }
        }, {
          label: 'Delete',
          dangerous: true,
          onClick: () => inUse ? setBlockedModalOpen(true) : setDeleteModalOpen(true)
        }]} />
      </TableCell>
    </TableRow>
    <ConfirmationModal title="Delete custom claim" buttonText="Delete claim"
                       description="Are you sure you want to delete this custom claim? It isn't mapped by any connection, so this won't affect any users."
                       open={deleteModalOpen}
                       onClose={() => {
                         setDeleteModalOpen(false)
                       }}
                       onConfirm={async () => {
                         await props.deleteClaim(name)
                       }}
    />
    <HankoDialog open={blockedModalOpen} onClose={() => setBlockedModalOpen(false)}>
      <Box sx={{ display: "flex", gap: "20px", flexDirection: 'column', maxWidth: '360px' }}>
        <Typography variant="h4" sx={{ overflowWrap: "anywhere" }}>Cannot delete <i>{name}</i></Typography>
        <Typography>This claim is still mapped by the following connections:</Typography>
        <Box component="ul" sx={{ m: 0, pl: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 1 }}>
          {referencedBy.map((reference, index) => (
            <Box component="li" key={`${reference.kind}-${reference.name}-${index}`}
                 sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography component="span" fontWeight={700}>{reference.name}</Typography>
              <Chip label={reference.kind} size="small" variant="outlined" />
            </Box>
          ))}
        </Box>
        <Typography>Remove the mapping on each one, then delete this claim.</Typography>
        <Button variant="contained" fullWidth onClick={() => setBlockedModalOpen(false)}>OK</Button>
      </Box>
    </HankoDialog>
  </>
}
